import React, { useState } from "react";
import { Form, Button } from "react-bootstrap";
import { FaSyncAlt } from "react-icons/fa";
import useCheckerManager from "../../../hooks/useCheckerManager";
import { useLocalStorage } from "../../../hooks/useLocalStorage";
import { storageKeys } from "../../../constants/storageKeys";
import initialSettings from "../../../constants/settings.json";

const CheckerManagerSettings = () => {
  const [loading, setLoading] = useState(false);
  const [checkersRegistryBaseURL, setCheckersRegistryBaseURL] = useLocalStorage(
    storageKeys.CHECKERS_REGISTRY_BASEURL,
    JSON.parse(initialSettings.localStorage.CHECKERS_REGISTRY_BASEURL)
  );
  // Add option SHOW_CHECKERS_WARNINGS from local storage
  const [showCheckersWarnings, setShowCheckersWarnings] = useLocalStorage(
    storageKeys.SHOW_CHECKERS_WARNINGS,
    JSON.parse(initialSettings.localStorage.SHOW_CHECKERS_WARNINGS)
  );

  const { checkers, fetchCheckers, toggleCheckerSelectionState } =
    useCheckerManager();

  /**
   * Handles the discovery (fetching) of available checkers from the registry.
   * - Sets loading state to true while fetching.
   * - Calls fetchCheckers to update the checkers state.
   * - If an error occurs, alerts the user with the error message.
   * - Always sets loading state back to false after operation.
   */
  const handleDiscoverCheckers = async () => {
    setLoading(true);
    try {
      await fetchCheckers(); // Fetch and update checkers state
    } catch (error) {
      alert(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Form>
        <div
          className="d-flex flex-wrap align-items-end gap-2 mb-3"
          style={{ rowGap: 12 }}
        >
          <Form.Group
            className="flex-grow-1"
            style={{ minWidth: 220, marginBottom: 0, flexBasis: "250px" }}
          >
            <Form.Label>
              <strong>Checkers Repository Base URL</strong>
            </Form.Label>
            <Form.Control
              value={checkersRegistryBaseURL}
              onChange={(e) => setCheckersRegistryBaseURL(e.target.value)}
              placeholder="Enter checkers registry URL"
            />
          </Form.Group>
          <div style={{ flexShrink: 0 }}>
            <Button
              size="sm"
              className="mb-1 w-100"
              onClick={handleDiscoverCheckers}
              disabled={loading || !checkersRegistryBaseURL}
              variant="primary"
              style={{ whiteSpace: "nowrap", minWidth: 120 }}
            >
              <FaSyncAlt className="me-1" />
              {loading ? "Fetching..." : "Fetch Checkers"}
            </Button>
          </div>
        </div>

        <Form.Group className="mb-3">
          <Form.Check
            type="switch"
            id="show-checkers-warnings-switch"
            label={<span>Hide Checkers Warnings</span>}
            checked={!showCheckersWarnings}
            onChange={(e) => setShowCheckersWarnings(!e.target.checked)}
          />
        </Form.Group>

        {checkers.length > 0 && (
          <div className="mt-4">
            <Form.Label>
              <strong>Available Checkers</strong>
            </Form.Label>
            <div className="p-3 bg-light" style={{ maxHeight: 250, overflowY: "auto" }}>
              {checkers.map((checker) => (
                <Form.Group
                  controlId={checker.key}
                  key={checker.id}
                  className="mb-2"
                >
                  <Form.Check
                    type="checkbox"
                    label={
                      <div>
                        <strong>{checker.name}</strong>
                        {checker.description && (
                          <div>
                            <small className="text-muted">{checker.description}</small>
                          </div>
                        )}
                      </div>
                    }
                    name={checker.key}
                    checked={checker.selected}
                    onChange={() => toggleCheckerSelectionState(checker.id)}
                  />
                </Form.Group>
              ))}
            </div>
          </div>
        )}
      </Form>
    </>
  );
};

export default CheckerManagerSettings;